import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Sparkle, Plus, X, ArrowsClockwise, TrendUp, TrendDown } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../../lib/api";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

/**
 * AI Budget Allocator (P1)
 * Admin enters a TOTAL monthly budget and CURRENT spend per channel.
 * → POST /api/admin/leads/budget-allocator
 * Backend joins historical lead-source metrics and asks Claude to redistribute
 * the budget to maximise high-score lead acquisition.
 */
export default function BudgetAllocatorPanel() {
  const [totalBudget, setTotalBudget] = useState(10000);
  const rowIdSeq = useRef(1);
  const nextRowId = () => `r${rowIdSeq.current++}`;
  const [rows, setRows] = useState([
    { id: nextRowId(), ref: "direct", spend: 2000 },
  ]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Pre-fill known channels from /admin/leads/sources once on mount.
  // `api` is a module-level singleton (not a reactive value) → empty deps is correct.
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/leads/sources?limit=10")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        if (items.length === 0) return;
        setRows(items.slice(0, 4).map((it) => ({
          id: nextRowId(),
          ref: it.ref,
          spend: it.ref === "direct" ? 2000 : 1000,
        })));
      })
      .catch(() => null);
    return () => { cancelled = true; };
  }, []);

  const updateRow = (id, key, val) => {
    setRows((r) => r.map((row) => row.id === id ? { ...row, [key]: val } : row));
  };
  const addRow = () => setRows((r) => [...r, { id: nextRowId(), ref: "", spend: 0 }]);
  const delRow = (id) => setRows((r) => r.filter((row) => row.id !== id));

  const totalSpend = rows.reduce((sum, r) => sum + (Number(r.spend) || 0), 0);

  const run = async () => {
    setError("");
    const current_spend = {};
    for (const r of rows) {
      const ref = String(r.ref || "").trim();
      if (!ref) continue;
      current_spend[ref] = Math.max(0, Number(r.spend) || 0);
    }
    if (Object.keys(current_spend).length === 0) {
      toast.error("Add at least one channel with a name.");
      return;
    }
    if (!totalBudget || totalBudget <= 0) {
      toast.error("Total budget must be > 0.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/leads/budget-allocator", {
        total_budget: Number(totalBudget),
        current_spend,
      });
      setResult(data);
      toast.success("New allocation ready");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail, errContext(err));
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border border-zinc-950 mt-8" data-testid="budget-allocator-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span><Sparkle size={12} className="inline mr-2" /> ai_budget_allocator</span>
        <span className="text-[10px] text-zinc-400">CLAUDE_SONNET_4.5</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr]">
        {/* INPUT FORM */}
        <div className="border-r border-zinc-200 p-5 bg-zinc-50/50">
          <div className="text-[10px] font-mono tracking-[0.18em] text-zinc-500 uppercase">total_monthly_budget</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-display text-2xl font-black">$</span>
            <input
              data-testid="budget-total-input"
              type="number" min="0" step="100"
              value={totalBudget}
              onChange={(e) => setTotalBudget(e.target.value)}
              className="font-display text-2xl font-black tnum border-b-2 border-zinc-950 outline-none bg-transparent w-full focus:border-[#FF3E00]"
            />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Current total spend: <strong className="tnum">{fmt$(totalSpend)}</strong>
            {totalSpend !== Number(totalBudget) && Number(totalBudget) > 0 && (
              <span className={`ml-2 font-mono text-[10px] ${totalSpend > totalBudget ? "text-[#D0021B]" : "text-amber-700"}`}>
                · {totalSpend > totalBudget ? "OVER" : "UNDER"} BUDGET BY {fmt$(Math.abs(totalSpend - totalBudget))}
              </span>
            )}
          </div>

          <div className="mt-5 text-[10px] font-mono tracking-[0.18em] text-zinc-500 uppercase">channel_spend</div>
          <div className="space-y-2 mt-2">
            {rows.map((r, i) => (
              <div key={r.id} className="flex items-center gap-2" data-testid={`budget-row-${i}`}>
                <input
                  data-testid={`budget-row-${i}-ref`}
                  type="text" placeholder="ref (e.g. twitter.com)"
                  value={r.ref}
                  onChange={(e) => updateRow(r.id, "ref", e.target.value)}
                  className="font-mono text-xs border border-zinc-300 px-2 py-1.5 flex-1 outline-none focus:border-zinc-950"
                />
                <span className="font-mono text-xs text-zinc-400">$</span>
                <input
                  data-testid={`budget-row-${i}-spend`}
                  type="number" min="0" step="10"
                  value={r.spend}
                  onChange={(e) => updateRow(r.id, "spend", e.target.value)}
                  className="font-mono text-xs tnum border border-zinc-300 px-2 py-1.5 w-24 outline-none focus:border-zinc-950 text-right"
                />
                {rows.length > 1 && (
                  <button onClick={() => delRow(r.id)} className="text-zinc-400 hover:text-[#D0021B]" data-testid={`budget-row-${i}-del`}>
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addRow}
              data-testid="budget-add-channel"
              className="text-xs font-mono text-zinc-500 hover:text-[#FF3E00] inline-flex items-center gap-1 mt-1">
              <Plus size={12} /> ADD CHANNEL
            </button>
          </div>

          <button
            onClick={run}
            disabled={loading}
            data-testid="budget-allocate-btn"
            className="mt-5 w-full bg-zinc-950 text-white font-mono text-[11px] tracking-[0.18em] uppercase py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            {loading ? <><ArrowsClockwise size={12} className="animate-spin" /> ALLOCATING…</> : "→ ALLOCATE WITH AI"}
          </button>
          {error && (
            <div className="mt-3 text-xs text-[#D0021B] font-mono" data-testid="budget-error">/ error: {error}</div>
          )}
        </div>

        {/* RESULT */}
        <div className="p-5">
          {!result ? (
            <div className="h-full flex items-center justify-center text-center min-h-[260px]" data-testid="budget-empty-state">
              <div>
                <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-300 uppercase">AI_RECOMMENDATION</div>
                <div className="font-display font-black text-2xl text-zinc-300 mt-2 leading-tight">
                  Enter your budget<br />and click allocate.
                </div>
                <div className="text-[11px] text-zinc-400 mt-2 max-w-xs mx-auto">
                  We&apos;ll cross-reference lead quality, count, and revenue per channel and recommend a redistribution.
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="budget-result">
              <div className="text-[10px] font-mono tracking-[0.18em] text-zinc-500 uppercase">/ ai_summary</div>
              <div className="text-sm leading-relaxed text-zinc-950 mt-1.5 border-l-2 border-[#FF3E00] pl-3" data-testid="budget-summary">
                {result.summary}
              </div>

              <div className="mt-5 text-[10px] font-mono tracking-[0.18em] text-zinc-500 uppercase">/ allocation</div>
              <table className="w-full text-sm mt-2">
                <thead className="border-b border-zinc-200 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">
                  <tr>
                    <th className="text-left py-2">Channel</th>
                    <th className="text-right py-2">Current</th>
                    <th className="text-right py-2">→ Recommended</th>
                    <th className="text-right py-2">Δ%</th>
                  </tr>
                </thead>
                <tbody data-testid="budget-recommendations-tbody">
                  {result.recommendations.map((r, i) => {
                    const up = r.delta_pct > 0;
                    const flat = r.delta_pct === 0;
                    return (
                      <tr key={r.ref + i} className="border-b border-zinc-100 align-top">
                        <td className="py-2.5 pr-2">
                          <div className="font-mono text-xs">{r.ref}</div>
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            {r.lead_count} leads · {r.signups ?? 0} signups ·{" "}
                            <span className={(r.paid || 0) > 0 ? "text-emerald-700 font-bold" : ""}>
                              {r.paid ?? 0} paid
                            </span>{" "}· score {r.avg_score}
                          </div>
                          <div className="text-[11px] text-zinc-600 mt-1 leading-snug max-w-xs">{r.rationale}</div>
                        </td>
                        <td className="py-2.5 text-right tnum text-zinc-500">{fmt$(r.current_spend)}</td>
                        <td className="py-2.5 text-right font-display font-black tnum text-zinc-950">{fmt$(r.recommended_spend)}</td>
                        <td className={`py-2.5 text-right tnum font-bold ${flat ? "text-zinc-400" : up ? "text-emerald-700" : "text-[#D0021B]"}`}>
                          <span className="inline-flex items-center gap-1">
                            {!flat && (up ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />)}
                            {r.delta_pct > 0 ? "+" : ""}{r.delta_pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-zinc-950">
                    <td className="py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">total</td>
                    <td className="py-2 text-right tnum text-zinc-500">{fmt$(totalSpend)}</td>
                    <td className="py-2 text-right tnum font-black">{fmt$(result.recommendations.reduce((s, r) => s + r.recommended_spend, 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>

              <div className="mt-3 text-[10px] font-mono text-zinc-400">
                generated_at: {new Date(result.generated_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
